import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Form, Input } from '@arco-design/web-react';
import { IconPlus } from '@arco-design/web-react/icon';

import type { AppRepoImageQuery } from '@/components-biz/AppRepoImageSelector';
import type { CommonImageItemType } from '@/components-biz/ImageSelect';
import { parseInferenceFrameworkImageItem } from '@/delivery-type-plugin/capabilities/maas-system-config/presetInferenceImage';
import { ResourceConfigCard } from '@/delivery-type-plugin/components';
import {
  buildImageResourceSummaryItems,
  resolveImageCardDisplayName,
} from '@/delivery-type-plugin/components/imageResourceSelect/buildImageSummaryItems';
import {
  SYSTEM_OPTIONS_CONTAINER_DEPLOY_CONFIG_FIELD,
  clearStoredContainerDeployConfig,
  writeStoredContainerDeployConfig,
} from '@/delivery-type-plugin/platform/container-deploy-config';

import { AppImageSourceModal } from './app-image-source-modal';
import { useAppImageSourceModal } from './useAppImageSourceModal';

const IMAGE_CARD_VENDOR_PREFIX = '__imageResourceCardPersist';
const IMAGE_CARD_ICON = 'icon-jingxiangmian';

export interface ImageSummarySource {
  displayName?: string;
  os?: string;
  version?: string;
  architecture?: string;
  systemDiskSize?: number | string;
}

export interface AppImageResourceSelectFieldProps {
  form: import('@arco-design/web-react/es/Form').FormInstance;
  queryObj?: AppRepoImageQuery;
  imageKeyField: string;
  imageUrlField: string;
  imagePrefix?: string;
  readOnly?: boolean;
  lockActionsWhenFilled?: boolean;
  /** SKU 预置是否已含镜像；与 lockActionsWhenFilled 联用 */
  presetHasImage?: boolean;
  summarySource?: ImageSummarySource | null;
  onImageChange?: (key: string, item: CommonImageItemType, imageUrl?: string) => void | Promise<void>;
  onImageClear?: () => void;
}

/** 应用镜像选择：ResourceConfigCard 回填 + 双来源弹窗（应用仓库 / 应用广场） */
export function AppImageResourceSelectField({
  form,
  queryObj = {},
  imageKeyField,
  imageUrlField,
  imagePrefix,
  readOnly = false,
  lockActionsWhenFilled = false,
  presetHasImage,
  summarySource,
  onImageChange,
  onImageClear,
}: AppImageResourceSelectFieldProps) {
  const { t } = useTranslation();
  const { modalVisible, modalSession, sourceTab, openModal, closeModal, handleSourceTabChange } = useAppImageSourceModal();
  const selectedKey = Form.useWatch(imageKeyField, form) as string | undefined;
  const selectedUrl = Form.useWatch(imageUrlField, form) as string | undefined;
  const inferenceFrameworkImageRaw = Form.useWatch('inferenceFrameworkImage', form);
  const normalizedPrefix = imagePrefix?.replace(/\.$/, '');
  const watchedImageName = Form.useWatch(normalizedPrefix ? `${normalizedPrefix}.imageName` : undefined, form) as string | undefined;
  const watchedLegacyName = Form.useWatch(normalizedPrefix ? `${normalizedPrefix}.name` : undefined, form) as string | undefined;

  const hasImage = Boolean(String(selectedKey ?? '').trim() || String(selectedUrl ?? '').trim());
  /** 弹窗确认需要完整 item；仅有 imageKey 高亮时用表单快照回填 */
  const initialSelectedItem = useMemo(() => {
    const item = parseInferenceFrameworkImageItem(inferenceFrameworkImageRaw);
    if (!item) return null;
    const key = String(selectedKey || '').trim();
    const itemKey = String(item.id ?? item.key ?? '').trim();
    if (key && itemKey && key !== itemKey) return null;
    return item;
  }, [inferenceFrameworkImageRaw, selectedKey]);
  /** 只读，或 SKU 预置已有镜像时锁删改（用户下单自选不锁） */
  const hideCardActions = readOnly || (lockActionsWhenFilled && presetHasImage === true);

  const summaryItems = useMemo(() => {
    if (summarySource) {
      return buildImageResourceSummaryItems(summarySource);
    }
    if (!hasImage) return [];
    const displayName = resolveImageCardDisplayName(form.getFieldsValue() as Record<string, unknown>, {
      imageKey: selectedKey,
      imageUrl: selectedUrl,
      imagePrefix,
    });
    return buildImageResourceSummaryItems({ displayName: displayName || '-' });
  }, [form, hasImage, imagePrefix, selectedKey, selectedUrl, summarySource, watchedImageName, watchedLegacyName]);

  const handleConfirm = useCallback(
    async (key: string, item: CommonImageItemType, imageUrl?: string) => {
      await onImageChange?.(key, item, imageUrl);
      form.setFieldValue(imageKeyField, key);
      form.setFieldValue(imageUrlField, imageUrl ?? item.dockerRefUrl ?? item.name);
      writeStoredContainerDeployConfig(form, item.containerDeployConfig);
      closeModal();
    },
    [closeModal, form, imageKeyField, imageUrlField, onImageChange],
  );

  const handleDelete = useCallback(() => {
    form.setFieldValue(imageKeyField, undefined);
    form.setFieldValue(imageUrlField, undefined);
    clearStoredContainerDeployConfig(form);
    onImageClear?.();
  }, [form, imageKeyField, imageUrlField, onImageClear]);

  const handleOpenModal = useCallback(() => {
    if (readOnly) return;
    openModal();
  }, [openModal, readOnly]);

  return (
    <div style={{ marginBottom: 16 }}>
      <Form.Item
        field={imageKeyField}
        label={t('common:image')}
        rules={
          readOnly
            ? undefined
            : [
                {
                  required: true,
                  message: t('common:validate.required.select', { name: t('common:image') }),
                },
              ]
        }
      >
        <Input style={{ display: 'none' }} />
        {hasImage ? (
          <ResourceConfigCard
            resourceCode="image"
            vendorDevicePrefix={IMAGE_CARD_VENDOR_PREFIX}
            icon={IMAGE_CARD_ICON}
            title={t('common:image')}
            items={summaryItems}
            canEdit={!hideCardActions}
            canDelete={!hideCardActions}
            onEdit={handleOpenModal}
            onDelete={handleDelete}
          />
        ) : readOnly ? null : (
          <Button type="dashed" long icon={<IconPlus />} onClick={handleOpenModal}>
            {t('common:image.select')}
          </Button>
        )}
      </Form.Item>
      <Form.Item field={SYSTEM_OPTIONS_CONTAINER_DEPLOY_CONFIG_FIELD} hidden>
        <Input />
      </Form.Item>
      {modalVisible ? (
        <AppImageSourceModal
          visible
          sourceTab={sourceTab}
          modalSession={modalSession}
          queryObj={queryObj}
          initialSelectedKey={selectedKey}
          initialSelectedItem={initialSelectedItem}
          onSourceTabChange={handleSourceTabChange}
          onCancel={closeModal}
          onConfirm={handleConfirm}
        />
      ) : null}
    </div>
  );
}
